import React, { useState } from 'react';
import Isvg from 'react-inlinesvg';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import MobileActionSheet from 'components/MobileActionSheet';
import { ICONS, PFE_ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import { selectIsMobile } from 'selectors/ui';

import { ITEM_AVAILABILITY_STATUS, ITEM_AVAILABILITY_STATUS_MAPPING } from 'features/Sku/Items/SkuItems.constants';
import { skuItemAvailabilityStatusMessages } from 'features/Sku/Items/SkuItems.messages';
import SkuItemAvailabilityStatus, { StatusMarker } from 'features/Sku/Items/components/SkuItemAvailabilityStatus';

import { getModifierAvailabilityStatus } from '../Modifiers.availability';
import { visibleModifiers } from '../Modifiers.formState';
import messages from '../ModifierLibrary.messages';
import RowActionsDropdown from './RowActionsDropdown';

const { element } = bem('modifier-library');

export const CELL_TOOLTIP = 'modifier-library-cell-tooltip';

const GROUP_AVAILABILITY_OPTIONS = [
  ITEM_AVAILABILITY_STATUS.AVAILABLE,
  ITEM_AVAILABILITY_STATUS.UNAVAILABLE_INDEFINITELY,
];

const groupShape = PropTypes.shape({
  core: PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }),
  attachedItems: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })),
  enabled: PropTypes.bool,
});

const ModifierRow = ({ group, isManual, onCellHover, onChangeAvailability, onEdit, onDuplicate, onDelete }) => {
  const intl = useIntl();
  const isMobile = useSelector(selectIsMobile);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const status = getModifierAvailabilityStatus(group);
  const { name } = group.core;
  const ariaLabel = intl.formatMessage(messages.actionsAriaLabel, { name });

  const sheetStatusOptions = GROUP_AVAILABILITY_OPTIONS.map(option => ({
    label: (
      <>
        <StatusMarker className={element('sheet-status-marker')} status={option} />
        {intl.formatMessage(skuItemAvailabilityStatusMessages[ITEM_AVAILABILITY_STATUS_MAPPING[option]?.message])}
      </>
    ),
    onClick: () => {
      if (option !== status) onChangeAvailability(option);
    },
  }));

  const optionNames = visibleModifiers(group)
    .map(modifier => modifier.core.name)
    .join(', ');
  const usedInNames = (group.attachedItems || []).map(item => item.name).join(', ');

  // React's mouseover handler flushes state before react-tooltip's own mouseenter listener runs,
  // so the shared tooltip is already enabled or disabled by the time it decides to open.
  const revealWhenTruncated = text => event => {
    const cell = event.currentTarget;
    onCellHover(cell.scrollWidth > cell.clientWidth ? text : '');
  };

  const activateRow = event => {
    if (event.key && event.key !== 'Enter' && event.key !== ' ') return;
    if (event.key) event.preventDefault();
    onEdit();
  };

  return (
    <tr
      className={element('row')}
      tabIndex={0}
      aria-label={intl.formatMessage(messages.rowAriaLabel, { name })}
      onClick={activateRow}
      onKeyDown={activateRow}>
      <td className={element('name-cell')} data-for={CELL_TOOLTIP} data-tip onMouseEnter={revealWhenTruncated(name)}>
        {name}
      </td>
      {!isMobile && (
        <td
          className={element('options-cell')}
          data-for={CELL_TOOLTIP}
          data-tip
          onMouseEnter={revealWhenTruncated(optionNames)}>
          {optionNames}
        </td>
      )}
      {!isMobile && (
        <td
          className={element('used-in-cell')}
          data-for={CELL_TOOLTIP}
          data-tip
          onMouseEnter={revealWhenTruncated(usedInNames)}>
          {usedInNames}
        </td>
      )}
      <td className={element('availability-cell')}>
        <SkuItemAvailabilityStatus
          status={status}
          statusOptions={GROUP_AVAILABILITY_OPTIONS}
          onChange={isManual ? onChangeAvailability : null}
          onMobileToggleClick={isManual && isMobile ? () => setIsSheetOpen(true) : null}
        />
      </td>
      {isManual && (
        // The sheet portal lives inside this cell so its taps bubble (through the React tree,
        // portals ignore the DOM tree) into this stopPropagation instead of the row's onClick.
        <td className={element('actions-cell')} onClick={event => event.stopPropagation()}>
          {isMobile ? (
            <button
              type="button"
              className={element('actions-button')}
              aria-label={ariaLabel}
              onClick={() => setIsSheetOpen(true)}>
              <Isvg src={PFE_ICONS.threeDots} />
            </button>
          ) : (
            <RowActionsDropdown ariaLabel={ariaLabel} onEdit={onEdit} onDuplicate={onDuplicate} onDelete={onDelete} />
          )}
          {isMobile && (
            <MobileActionSheet
              isOpen={isSheetOpen}
              onClose={() => setIsSheetOpen(false)}
              title={name}
              options={[
                [
                  ...sheetStatusOptions,
                  { icon: ICONS.copy2, label: intl.formatMessage(messages.duplicateModifier), onClick: onDuplicate },
                  { icon: ICONS.editPencilSku, label: intl.formatMessage(messages.editModifierGroup), onClick: onEdit },
                ],
                [
                  {
                    icon: ICONS.alertTriangle,
                    label: intl.formatMessage(messages.deleteModifierGroup),
                    onClick: onDelete,
                    danger: true,
                  },
                ],
              ]}
            />
          )}
        </td>
      )}
    </tr>
  );
};

ModifierRow.propTypes = {
  group: groupShape.isRequired,
  isManual: PropTypes.bool.isRequired,
  onCellHover: PropTypes.func.isRequired,
  onChangeAvailability: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default ModifierRow;
